"use client";

import { Leaf } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormControl, FormField, FormItem, FormMessage } from "@/components/ui/form";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const interestOptions = ["Tree Pruning", "Emergency Removal", "Stump Grinding", "Health Assessments"];
function NewsletterForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-6 border border-border rounded-md bg-card">
        <p className="font-default text-2xl font-bold text-foreground mb-3">Protocol Initiated.</p>
        <p className="text-muted-foreground mb-4 font-sans">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="rounded-md border-white text-white hover:bg-white hover:text-background transition-colors">
          Register Another Contact
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
      {}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <FormField control={control} name="first_name" render={({
        field
      }) => <FormItem className="space-y-2">
              <Label className="text-[10px] tracking-[0.2em] uppercase font-mono text-muted-foreground">
                01 // Full Name
              </Label>
              <FormControl>
                <Input type="text" placeholder="John Doe" className="border-0 border-b-2 border-secondary rounded-md bg-transparent px-0 h-11 focus-visible:ring-0 focus-visible:border-primary placeholder:text-muted-foreground/40 font-mono" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email"
        }
      }} render={({
        field
      }) => <FormItem className="space-y-2">
              <Label className="text-[10px] tracking-[0.2em] uppercase font-mono text-muted-foreground">
                02 // Email Address <span className="text-destructive">*</span>
              </Label>
              <FormControl>
                <Input type="email" placeholder="j.doe@arborpro.com" className="border-0 border-b-2 border-secondary rounded-md bg-transparent px-0 h-11 focus-visible:ring-0 focus-visible:border-primary placeholder:text-muted-foreground/40 font-mono" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      {}
      <FormField control={control} name="interests" render={({
      field
    }) => <FormItem className="space-y-3">
            <Label className="text-[10px] tracking-[0.2em] uppercase font-mono text-muted-foreground">
              03 // Service Interest
            </Label>
            <div className="grid grid-cols-2 gap-3">
              {interestOptions.map((option, i) => {
          const values: string[] = Array.isArray(field.value) ? field.value : [];
          const checked = values.includes(option);
          return <label key={option} data-index={i} className="flex items-center gap-2.5 cursor-pointer group">
                    <Checkbox checked={checked} onCheckedChange={c => {
              if (c) {
                field.onChange([...values, option]);
              } else {
                field.onChange(values.filter(v => v !== option));
              }
            }} className="border-secondary rounded-md data-[state=checked]:bg-primary data-[state=checked]:border-primary" />
                    <span className="text-sm font-mono text-foreground/80 group-hover:text-primary transition-colors">
                      {option}
                    </span>
                  </label>;
        })}
            </div>
            <p className="text-[10px] font-mono text-muted-foreground uppercase tracking-wider">
              Select all relevant categories for technical updates.
            </p>
            <FormMessage />
          </FormItem>} />

      {}
      <FormField control={control} name="consent" rules={{
      required: "Please agree to receive updates"
    }} render={({
      field
    }) => <FormItem>
            <div className="flex items-start gap-3">
              <Checkbox checked={!!field.value} onCheckedChange={c => field.onChange(!!c)} className="mt-1 border-secondary rounded-md data-[state=checked]:bg-primary data-[state=checked]:border-primary" />
              <Label className="text-sm text-foreground/80 font-sans font-normal leading-relaxed cursor-pointer">
                I consent to receive technical insights and service alerts from Arbor Pro.
              </Label>
            </div>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/30 rounded-md">
          <p className="text-destructive text-sm font-mono">{submitError}</p>
        </div>}

      <div className="pt-2">
        <Button type="submit" disabled={isSubmitting} size="lg" className="rounded-md px-8 h-12 bg-primary text-primary-foreground hover:bg-foreground hover:text-primary transition-all font-mono font-bold uppercase tracking-widest">
          {isSubmitting ? "PROCESSING..." : "JOIN THE NETWORK"}
        </Button>
        <p className="text-[10px] font-mono text-muted-foreground mt-4 uppercase tracking-widest">
          Surgical Precision. Zero Spam.
        </p>
      </div>
    </Form>;
}
export default function StayInformed() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>();
  return <section data-section-id="593" id="stay-informed" className="dark bg-background text-foreground py-24 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
      {}
      <div className="absolute top-8 right-8 opacity-10 hidden sm:block">
        <Leaf className="w-24 h-24 text-primary rotate-12" strokeWidth={0.5} />
      </div>

      <div ref={ref} className={`max-w-5xl mx-auto transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          {}
          <div className="lg:col-span-5">
            <div className="relative overflow-hidden rounded-md aspect-[4/5] lg:aspect-[3/4] w-full max-w-xs mx-auto lg:max-w-none border border-secondary/20">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1697657/img/IMG_5260.jpg" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1697657/img/IMG_5260.jpg-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1697657/img/IMG_5260.jpg-wvc-sizes" alt="Professional bucket truck and chipper operation with safety cones" loading="lazy" decoding="async" className="w-full h-full object-cover grayscale hover:grayscale-0 transition-all duration-700" />
              <div className="absolute top-4 right-4 text-primary font-mono text-xs">+</div>
              <div className="absolute bottom-4 left-4 text-primary font-mono text-xs">+</div>
            </div>
          </div>

          {}
          <div className="lg:col-span-7 lg:pl-4">
            {}
            <div className="flex items-center gap-3 mb-6">
              <span className="h-px w-8 bg-primary" />
              <span className="text-[11px] tracking-[0.25em] uppercase font-mono text-primary">
                04 // Technical Network
              </span>
            </div>

            {}
            <h2 className="font-default text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tighter leading-[0.95] text-foreground mb-6">
              Secure your canopy's{" "}
              <span className="text-primary">future health.</span>
            </h2>

            {}
            <p className="text-muted-foreground font-sans text-base sm:text-lg leading-relaxed mb-10 max-w-lg">
              Join our technical briefing for DFW homeowners. Receive seasonal maintenance protocols, emergency weather alerts, and priority scheduling for our surgical tree services.
            </p>

            {}
            <FormProvider formKey="stay_informed_form_tree_insights" formId="stay_informed_form_tree_insights" sectionName="stay-informed" defaultValues={{
            first_name: "",
            email: "",
            interests: [],
            consent: false
          }} validationRules={{
            email: {
              required: true,
              pattern: "email"
            },
            consent: {
              required: true
            }
          }} submitText="Join Network" successMessage="Registration complete. Your first technical briefing is en route to your inbox." errorMessage="System error. Please re-attempt registration.">
              <NewsletterForm />
            </FormProvider>
          </div>
        </div>
      </div>
    </section>;
}